/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Assignment Settings Form
 *
 * @author Satyaki Bose
 * @file AssignmentSettingsForm.jsx
 */

import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { Button, TextField, RadioGroup } from '@mui/material';
import mixins from '../../../assets/styles/mixins.scss';
import InlineActionAlert from '../../common/InlineActionAlert';
import AssignmentTimeField from '../AssignmentTimeField';
import QuickScheduleTimezoneField from '../../QuickSchedule/QuickScheduleTimezoneField';
import SettingsRadioCard from './SettingsRadioCard';
import SettingsSectionHeader from './SettingsSectionHeader';
import SettingsSelectField from './SettingsSelectField';
import LateWorkRuleRow from './LateWorkRuleRow';
import GradebookInfoModal from './GradebookInfoModal';
import { GRADEBOOK_DISPLAY_MODE, defaultConfig } from './utils/assignmentSettingsConstants';
import {
  computeAssignmentSettingsVisibility,
  mergeAssignmentSettingsConfig
} from './utils/assignmentSettingsFormConfig';
import {
  buildFormDefaultValues,
  isSaveDisabled,
  normalizeBaseValueInput
} from './utils/assignmentSettingsFormUtils';
import {
  createBaseValueValidator,
  createLateWorkCustomDateValidator,
  createLateWorkPercentValidator
} from './utils/assignmentSettingsFormValidation';
import {
  AssignmentSettingsCancelButton,
  AssignmentSettingsFormStylesRoot,
  classes
} from './styles/assignmentSettingsFormStyles';

// every defaultConfig entry is a visibility flag, so derive the shape from it directly
const assignmentSettingsConfigShape = PropTypes.shape(
  Object.keys(defaultConfig).reduce((shape, key) => {
    // eslint-disable-next-line no-param-reassign
    shape[key] = PropTypes.bool;

    return shape;
  }, {})
);

const AssignmentSettingsForm = ({
  initialValues,
  textInfo,
  formKey,
  timeZoneOptions,
  lateWorkDeadlineOptions,
  assignmentAvailabilityOptions,
  isSubmitting,
  showInfoModal,
  renderGradebookInfoModal,
  config: configProp,
  callback
}) => {
  const { config, visibility } = useMemo(() => {
    const merged = mergeAssignmentSettingsConfig(configProp);

    return {
      config: merged,
      visibility: computeAssignmentSettingsVisibility(merged)
    };
  }, [configProp]);
  const selectConfig = useMemo(() => ({
    showSelectedCheckIcon: config.showSelectCheckIcon !== false
  }), [config.showSelectCheckIcon]);
  const formDefaults = buildFormDefaultValues(initialValues);
  const fieldsDisabled = config.disableAllFields;

  const {
    control,
    watch,
    reset,
    getValues,
    setValue,
    trigger,
    formState: { isValid, isDirty }
  } = useForm({
    defaultValues: formDefaults,
    mode: 'onChange'
  });

  const values = watch();
  const isEqualWeighting = values.gradebookDisplayMode === GRADEBOOK_DISPLAY_MODE.EQUAL_WEIGHTING;

  const baseValueRules = useMemo(
    () => ({ validate: createBaseValueValidator(textInfo, visibility) }),
    [textInfo, visibility]
  );
  const lateWorkPercentRules = useMemo(
    () => createLateWorkPercentValidator(textInfo, visibility),
    [textInfo, visibility]
  );
  const lateWorkCustomDateRules = useMemo(
    () => createLateWorkCustomDateValidator(textInfo, visibility),
    [textInfo, visibility]
  );

  useEffect(() => {
    reset(buildFormDefaultValues(initialValues));
    trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset on formKey only
  }, [formKey, reset, trigger]);

  useEffect(() => {
    trigger(['equalWeightingBaseValue', 'lateWorkPercent', 'lateWorkCustomDate']);
  }, [
    values.gradebookDisplayMode,
    values.allowLateWork,
    values.lateWorkDeadlineType,
    visibility.isEqualWeightingBaseValueVisible,
    visibility.isLateWorkPercentFieldVisible,
    visibility.isLateWorkCustomDateFieldVisible,
    trigger
  ]);

  const notifyFieldChange = (fieldName, value) => {
    callback?.onFieldChange?.(fieldName, value, getValues());
  };

  const handleDisplayModeSelect = (mode) => {
    setValue('gradebookDisplayMode', mode, { shouldValidate: true, shouldDirty: true });
    callback?.onGradebookDisplayModeChange?.(mode);
    notifyFieldChange('gradebookDisplayMode', mode);
  };

  const handleAllowLateWorkChange = (checked) => {
    setValue('allowLateWork', checked, { shouldValidate: true, shouldDirty: true });
    callback?.onAllowLateWorkChange?.(checked);
    notifyFieldChange('allowLateWork', checked);
  };

  const handleLateWorkDeadlineChange = (deadlineType) => {
    callback?.onLateWorkDeadlineChange?.(deadlineType);
  };

  const handleSave = () => {
    callback?.onSave?.(getValues());
  };

  const handleCancel = () => {
    callback?.onCancel?.();
  };

  const saveDisabled = isSaveDisabled({
    isValid,
    isSubmitting,
    disableAllFields: fieldsDisabled,
    requireDirtyToSave: config.requireDirtyToSave,
    isDirty
  });

  const renderAlertBanner = () => {
    if (!visibility.showAlertBanner) {
      return null;
    }

    return (
      <InlineActionAlert
        message={textInfo.lmsAlertMessage}
        accentColor={mixins.linkDefault}
        className={classes.alertBanner}
      />
    );
  };

  const renderGradebookSection = () => (
    <div className={classes.section}>
      {visibility.isGradebookSectionTitleVisible ? (
        <SettingsSectionHeader
          classes={classes}
          title={textInfo.gradebookSectionTitle}
          showGradebookInfoButton={visibility.isGradebookInfoButtonVisible}
          onInfoClick={callback?.onGradebookInfoOpen}
          infoButtonAriaLabel={textInfo.gradebookInfoButtonAriaLabel}
        />
      ) : null}

      <RadioGroup
        row
        className={classes.radioCardsRow}
      >
        {visibility.isRawScoreOptionVisible ? (
          <SettingsRadioCard
            id={GRADEBOOK_DISPLAY_MODE.RAW_SCORE}
            title={textInfo.rawScoreTitle}
            description={textInfo.rawScoreDescription}
            selected={values.gradebookDisplayMode === GRADEBOOK_DISPLAY_MODE.RAW_SCORE}
            disabled={fieldsDisabled}
            onSelect={handleDisplayModeSelect}
          />
        ) : null}
        {visibility.isEqualWeightingOptionVisible ? (
          <SettingsRadioCard
            id={GRADEBOOK_DISPLAY_MODE.EQUAL_WEIGHTING}
            title={textInfo.equalWeightingTitle}
            description={textInfo.equalWeightingDescription}
            selected={isEqualWeighting}
            disabled={fieldsDisabled}
            onSelect={handleDisplayModeSelect}
          >
            {visibility.isEqualWeightingBaseValueVisible ? (
              <>
                <span className={classes.extraLabel}>{textInfo.baseValueLabel}</span>
                <Controller
                  name="equalWeightingBaseValue"
                  control={control}
                  rules={baseValueRules}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      variant="outlined"
                      className={[
                        classes.equalWeightingBaseValueField,
                        !isEqualWeighting || fieldsDisabled ? classes.equalWeightingBaseValueFieldDisabled : ''
                      ].filter(Boolean).join(' ')}
                      disabled={!isEqualWeighting || fieldsDisabled}
                      inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                        maxLength: 3,
                        'aria-label': textInfo.baseValueLabel
                      }}
                      onChange={(event) => {
                        const nextValue = normalizeBaseValueInput(event.target.value);
                        field.onChange(nextValue);
                        notifyFieldChange('equalWeightingBaseValue', nextValue);
                      }}
                    />
                  )}
                />
              </>
            ) : null}
          </SettingsRadioCard>
        ) : null}
      </RadioGroup>
    </div>
  );

  const renderDueDatesSection = () => (
    <div className={classes.section}>
      {visibility.isDueDatesSectionTitleVisible ? (
        <SettingsSectionHeader
          classes={classes}
          title={textInfo.dueDatesSectionTitle}
        />
      ) : null}
      <div className={classes.dueDateRow}>
        {visibility.isDefaultDueTimeFieldVisible ? (
          <div className={classes.dueDateField}>
            <AssignmentTimeField
              name="defaultDueTime"
              control={control}
              label={textInfo.defaultDueTimeLabel}
              onAfterChange={() => notifyFieldChange('defaultDueTime', getValues('defaultDueTime'))}
            />
          </div>
        ) : null}
        {visibility.isCourseTimeZoneFieldVisible ? (
          <div className={classes.dueDateField}>
            <QuickScheduleTimezoneField
              name="courseTimeZone"
              control={control}
              label={textInfo.courseTimeZoneLabel}
              options={timeZoneOptions}
              onChange={(value) => notifyFieldChange('courseTimeZone', value)}
            />
          </div>
        ) : null}
      </div>
    </div>
  );

  const renderAvailabilitySection = () => (
    <div className={classes.section}>
      {visibility.isAvailabilitySectionTitleVisible ? (
        <SettingsSectionHeader
          classes={classes}
          title={textInfo.availabilitySectionTitle}
        />
      ) : null}
      {visibility.isAvailabilityFieldVisible ? (
        <Controller
          name="assignmentAvailability"
          control={control}
          render={({ field }) => (
            <SettingsSelectField
              className={classes.dropdownField}
              label={textInfo.availabilityFieldLabel}
              value={field.value}
              options={assignmentAvailabilityOptions}
              disabled={fieldsDisabled}
              config={selectConfig}
              onChange={(value) => {
                field.onChange(value);
                notifyFieldChange('assignmentAvailability', value);
              }}
            />
          )}
        />
      ) : null}
    </div>
  );

  const renderLateWorkSection = () => (
    <div className={classes.section}>
      {visibility.isLateWorkSectionTitleVisible ? (
        <SettingsSectionHeader
          classes={classes}
          title={textInfo.lateWorkSectionTitle}
        />
      ) : null}
      <LateWorkRuleRow
        classes={classes}
        control={control}
        textInfo={textInfo}
        disabled={fieldsDisabled}
        visibility={visibility}
        selectConfig={selectConfig}
        lateWorkDeadlineOptions={lateWorkDeadlineOptions}
        allowLateWork={values.allowLateWork}
        lateWorkPercentRules={lateWorkPercentRules}
        lateWorkCustomDateRules={lateWorkCustomDateRules}
        onAllowLateWorkChange={handleAllowLateWorkChange}
        onLateWorkDeadlineChange={handleLateWorkDeadlineChange}
        onFieldChange={notifyFieldChange}
      />
    </div>
  );

  const renderGradebookAndDueDatesSection = () => (
    <div className={classes.sectionGroup}>
      {visibility.isGradebookSectionVisible ? renderGradebookSection() : null}
      {visibility.isDueDatesSectionVisible ? renderDueDatesSection() : null}
    </div>
  );

  const renderActions = () => {
    if (!visibility.showFormActions) {
      return null;
    }

    return (
      <div className={classes.actionsRow}>
        {visibility.showCancelButton ? (
          <AssignmentSettingsCancelButton
            type="button"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            <span>{textInfo.cancelButtonLabel}</span>
          </AssignmentSettingsCancelButton>
        ) : null}
        {visibility.showSaveButton ? (
          <Button
            type="button"
            className={classes.saveButton}
            onClick={handleSave}
            disabled={saveDisabled}
          >
            {textInfo.saveButtonLabel}
          </Button>
        ) : null}
      </div>
    );
  };

  const formSections = [
    {
      key: 'gradebookAndDueDates',
      visible: visibility.isGradebookSectionVisible || visibility.isDueDatesSectionVisible,
      render: renderGradebookAndDueDatesSection
    },
    {
      key: 'lateWork',
      visible: visibility.isLateWorkSectionVisible,
      render: renderLateWorkSection
    },
    {
      key: 'availability',
      visible: visibility.isAvailabilitySectionVisible,
      render: renderAvailabilitySection
    }
  ].filter((section) => section.visible);

  const isInfoModalEnabled = visibility.showGradebookInfoModal;

  return (
    <AssignmentSettingsFormStylesRoot className={classes.root}>
      {renderAlertBanner()}
      <div className={classes.formPane}>
        <div className={classes.formBody}>
          {formSections.map((section, index) => (
            <React.Fragment key={section.key}>
              {index > 0 ? <hr className={classes.divider} /> : null}
              {section.render()}
            </React.Fragment>
          ))}
        </div>
        {renderActions()}
      </div>

      {isInfoModalEnabled && renderGradebookInfoModal ? (
        <GradebookInfoModal
          classes={classes}
          open={Boolean(showInfoModal)}
          textInfo={textInfo}
          onClose={callback?.onGradebookInfoClose}
        />
      ) : null}
    </AssignmentSettingsFormStylesRoot>
  );
};

AssignmentSettingsForm.propTypes = {
  initialValues: PropTypes.object,
  textInfo: PropTypes.object.isRequired,
  formKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  timeZoneOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  })),
  lateWorkDeadlineOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  })),
  assignmentAvailabilityOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  })),
  isSubmitting: PropTypes.bool,
  showInfoModal: PropTypes.bool,
  renderGradebookInfoModal: PropTypes.bool,
  config: assignmentSettingsConfigShape,
  callback: PropTypes.shape({
    onCancel: PropTypes.func,
    onSave: PropTypes.func,
    onFieldChange: PropTypes.func,
    onGradebookDisplayModeChange: PropTypes.func,
    onGradebookInfoOpen: PropTypes.func,
    onGradebookInfoClose: PropTypes.func,
    onAllowLateWorkChange: PropTypes.func,
    onLateWorkDeadlineChange: PropTypes.func
  }).isRequired
};

AssignmentSettingsForm.defaultProps = {
  initialValues: {},
  formKey: 0,
  timeZoneOptions: [],
  lateWorkDeadlineOptions: [],
  assignmentAvailabilityOptions: [],
  isSubmitting: false,
  showInfoModal: false,
  renderGradebookInfoModal: true,
  config: {}
};

export default AssignmentSettingsForm;
